import type { CalendarEventSearchResult } from '@/api/search';
import { fetchTodoTask } from '@/api/todoApi';
import { useUIStore } from '@/stores/uiStore';
import { useTodoViewStore } from '@/stores/todoViewStore';
import { useCalendarViewStore } from '@/stores/calendarViewStore';
import { useSearchStore } from '@/stores/searchStore';
import { useTodoStore } from '@/stores/todoStore';
import type { TodoTask } from '@/types/todo';
import { buildTasksPath, buildCalendarPath } from '@/lib/urlRouting';
import { safePushState } from '@/lib/iosPwaNavigation';

/**
 * Navigate to a task from any workspace.
 * Clears search, switches to todo workspace, navigates to appropriate view, and selects the task.
 * Handles different task states: open, someday, completed, cancelled, deleted.
 */
export function navigateToTask(taskId: string): void {
  useSearchStore.getState().clearSearch();
  useUIStore.getState().setActiveWorkspace('todo');

  // Update URL to task path
  const taskUrl = buildTasksPath({ taskId });
  safePushState(taskUrl);

  // Look up task from store first
  let task = useTodoStore.getState().tasks[taskId] ?? null;

  if (task) {
    navigateToTaskView(task);
  } else {
    // Task not in store, fetch it and then navigate
    void fetchAndNavigateToTask(taskId);
  }
}

/**
 * Navigate to the appropriate view based on task status and properties.
 */
function navigateToTaskView(task: TodoTask): void {
  const todoViewStore = useTodoViewStore.getState();

  if (task.status === 'deleted') {
    // Deleted tasks are in trash view
    todoViewStore.setActiveView('trash');
  } else if (task.status === 'completed' || task.status === 'cancelled') {
    // Completed/cancelled tasks are in logbook view
    todoViewStore.setActiveView('logbook');
  } else if (task.isSomeday) {
    // Someday tasks need the someday view
    todoViewStore.setActiveView('someday', {
      listId: task.listId,
      projectId: task.projectId ?? null,
    });
  } else {
    // Open, non-someday tasks go to anytime view with list/project filter
    // This will use computeListProjectView which shows ALL open non-someday tasks
    todoViewStore.setActiveView('anytime', {
      listId: task.listId,
      projectId: task.projectId ?? null,
    });
  }

  // Set selected task AFTER setActiveView (which clears selection)
  todoViewStore.setSelectedTaskId(task.id);
}

/**
 * Fetch a task from the API and navigate to it.
 * Used when the task isn't already in the store.
 */
async function fetchAndNavigateToTask(taskId: string): Promise<void> {
  try {
    const task = await fetchTodoTask(taskId);
    // Add task to store so it's available
    useTodoStore.getState().upsertTask(task);
    navigateToTaskView(task);
  } catch (error) {
    console.error('Failed to fetch task for navigation:', error);
    // Still try to select the task in case it becomes available
    useTodoViewStore.getState().setSelectedTaskId(taskId);
  }
}

/**
 * Navigate to a calendar event from any workspace.
 * Clears search, switches to calendar workspace, sets focus date, and opens event detail.
 */
export function navigateToCalendarEvent(
  event: CalendarEventSearchResult['calendar_event']
): void {
  useSearchStore.getState().clearSearch();
  useUIStore.getState().setActiveWorkspace('calendar');
  const eventDate = event.startDateTime || event.startDate;
  if (eventDate) {
    const dateStr = eventDate.slice(0, 10);

    // Update URL to calendar path with date and current view
    const currentView = useCalendarViewStore.getState().activeView;
    const calendarUrl = buildCalendarPath({ date: dateStr, view: currentView });
    safePushState(calendarUrl);

    useCalendarViewStore.getState().setFocusDate(dateStr);
    useCalendarViewStore.getState().openEventDetail(event.googleEventId);
  }
}

/**
 * Navigate to mail workspace.
 * Clears search and switches to mail workspace with mailbox view.
 */
export function navigateToMail(): void {
  useSearchStore.getState().clearSearch();
  useUIStore.getState().setActiveWorkspace('mail');
  useUIStore.getState().setView('mailbox');

  // Navigate to root mail path - the MailboxPage will handle specific routing
  safePushState('#/');
}
